/**
 * FeedbackCard - Molecule component for displaying individual feedback items as accordions
 * Following UX.xml principles: Progressive disclosure, clear hierarchy, consistent interactions
 */
import React, { useState } from 'react';
import FeedbackStatus, { FeedbackStatusType } from '../atoms/FeedbackStatus';
import UserAvatar from '../atoms/UserAvatar';
import { formatDistanceToNow, format } from 'date-fns';
import { getDisplayName, getUserTooltip } from '../../utils/userDisplay';

export type FeedbackPriorityType = 'low' | 'medium' | 'high' | 'urgent';

interface UserSummary {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AppSummary {
  client_id: string;
  name: string;
  description?: string;
  logo_uri?: string;
}

interface CommentSummary {
  id: number;
  content: string;
  user_id: string;
  created_at: string;
  author?: {
    auth0_user_id: string;
    name: string;
    email: string;
    picture: string;
  };
}

interface SentimentData {
  sentiment: 'positive' | 'negative' | 'neutral';
  confidence: number;
  themes: string[];
  summary: string;
  recommendations: string[];
  analyzed_at: string;
  model_version: string;
  context_analysis?: string;
}

interface FeedbackData {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatusType;
  priority: FeedbackPriorityType;
  created_at: string;
  updated_at?: string;
  user_id?: string;
  app_id?: string;
  user?: UserSummary;
  app?: AppSummary;
  assigned_to?: string;
  assigned_user?: UserSummary;
  resolved_at?: string;
  resolved_by?: string;
  resolver?: UserSummary;
  external_id?: string;
  source_url?: string;
  extra_data?: any;
  is_internal?: boolean;
  is_public?: boolean;
  comments?: CommentSummary[];
  sentiment_analysis?: SentimentData;
}

interface FeedbackCardProps {
  feedback: FeedbackData;
  onClick?: (feedback: FeedbackData) => void;
  onStatusChange?: (id: number, status: FeedbackStatusType) => void;
  onPriorityChange?: (id: number, priority: FeedbackPriorityType) => void;
  defaultExpanded?: boolean;
  compact?: boolean;
  className?: string;
}

// Using brand colors from globals.css
const priorityConfig = {
  low: { color: 'text-grey-600 dark:text-grey-400', icon: '🔹', bgColor: 'bg-grey-50 dark:bg-grey-900/20' },
  medium: { color: 'text-blue-600 dark:text-blue-400', icon: '🔶', bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  high: { color: 'text-orange-600 dark:text-orange-400', icon: '🔥', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
  urgent: { color: 'text-error-500 dark:text-red-400', icon: '🚨', bgColor: 'bg-red-50 dark:bg-red-900/20' }
};

// Following UX.xml color psychology principles
const sentimentConfig = {
  positive: { 
    color: 'text-green-700 dark:text-green-400', 
    icon: '😊', 
    bgColor: 'bg-green-50 dark:bg-green-900/20',
    borderColor: 'border-green-200 dark:border-green-700/50'
  },
  negative: { 
    color: 'text-red-700 dark:text-red-400', 
    icon: '😔', 
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-700/50'
  },
  neutral: { 
    color: 'text-grey-700 dark:text-grey-400', 
    icon: '😐', 
    bgColor: 'bg-grey-50 dark:bg-grey-800/30',
    borderColor: 'border-grey-200 dark:border-grey-600/50'
  }
};

// Detailed Sentiment Analysis Component
const SentimentAnalysis: React.FC<{ sentiment: SentimentData }> = ({ sentiment }) => {
  const config = sentimentConfig[sentiment.sentiment];
  const confidencePercent = Math.round(sentiment.confidence * 100);
  
  return (
    <div className="bg-stone-50 dark:bg-slate-900/50 rounded-lg border border-stone-200 dark:border-slate-700 p-4 mb-4">
      <h4 className="text-base font-semibold text-slate-900 dark:text-stone-100 mb-3 flex items-center">
        <span className="mr-2">🤖</span>
        AI Sentiment Analysis
        <span className="ml-2 text-xs text-grey-500 dark:text-grey-400 font-normal">
          ({confidencePercent}% confidence)
        </span>
      </h4>
      
      <div className="space-y-3">
        {/* Sentiment Badge with Summary */}
        <div className={`flex items-start space-x-3 p-3 rounded-lg border ${config.bgColor} ${config.borderColor}`}>
          <span className="text-lg mt-0.5">{config.icon}</span>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <span className={`text-sm font-semibold ${config.color}`}>
                {sentiment.sentiment.charAt(0).toUpperCase() + sentiment.sentiment.slice(1)} Sentiment
              </span>
            </div>
            <p className={`text-sm ${config.color} opacity-90`}>
              {sentiment.summary}
            </p>
          </div>
        </div>

        {/* Context Analysis */}
        {sentiment.context_analysis && (
          <div className="bg-white dark:bg-slate-800 rounded-lg border border-stone-200 dark:border-slate-600 p-3">
            <h5 className="text-sm font-medium text-slate-700 dark:text-stone-300 mb-2 flex items-center">
              <span className="mr-1">💭</span>
              Context Analysis
            </h5>
            <p className="text-sm text-grey-600 dark:text-grey-400 leading-relaxed">
              {sentiment.context_analysis}
            </p>
          </div>
        )}
        
        {/* Key Themes */}
        {sentiment.themes && sentiment.themes.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-slate-700 dark:text-stone-300 mb-2 flex items-center">
              <span className="mr-1">🏷️</span>
              Key Themes ({sentiment.themes.length})
            </h5>
            <div className="flex flex-wrap gap-2">
              {sentiment.themes.map((theme, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs rounded-full border border-blue-200 dark:border-blue-700"
                >
                  {theme}
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Actionable Recommendations */}
        {sentiment.recommendations && sentiment.recommendations.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-slate-700 dark:text-stone-300 mb-2 flex items-center">
              <span className="mr-1">💡</span>
              Recommendations ({sentiment.recommendations.length})
            </h5>
            <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg border border-blue-200 dark:border-blue-700/50 p-3">
              <ul className="space-y-2">
                {sentiment.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-slate-700 dark:text-stone-300 flex items-start">
                    <span className="text-blue-500 dark:text-blue-400 mr-2 mt-0.5 font-bold">
                      {index + 1}.
                    </span>
                    <span className="flex-1">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {/* Analysis Metadata */}
        <div className="pt-2 border-t border-stone-200 dark:border-slate-700">
          <div className="flex items-center justify-between text-xs text-grey-500 dark:text-grey-400">
            <span>
              Analyzed {formatDistanceToNow(new Date(sentiment.analyzed_at), { addSuffix: true })}
            </span>
            <span className="bg-stone-200 dark:bg-slate-700 px-2 py-1 rounded text-xs">
              {sentiment.model_version}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Consistent section heading used throughout the expanded card body
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-xs font-semibold uppercase tracking-wide text-grey-500 dark:text-grey-400 mb-2">
    {children}
  </h4>
);

// Shared styling for the white content panels so padding/borders stay uniform
const PANEL_CLASS =
  'bg-white dark:bg-slate-800 rounded-lg border border-stone-200 dark:border-slate-600 p-4';

const FeedbackCard: React.FC<FeedbackCardProps> = ({
  feedback,
  onClick,
  onStatusChange,
  onPriorityChange,
  defaultExpanded = false,
  compact = false,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const priorityConfig_item = priorityConfig[feedback.priority];
  
  const handleExpandToggle = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(feedback);
    }
  };

  const handleStatusChange = (newStatus: FeedbackStatusType) => {
    onStatusChange?.(feedback.id, newStatus);
  };

  const handlePriorityChange = (newPriority: FeedbackPriorityType) => {
    onPriorityChange?.(feedback.id, newPriority);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: format(date, 'PPpp')
    };
  };

  return (
    <div className={`card transition-theme ${className}`}>
      {/* Collapsed Header - Always Visible */}
      <div 
        className={`flex items-center justify-between cursor-pointer hover:bg-stone-50 dark:hover:bg-slate-800/50 transition-colors ${compact ? 'p-3' : 'p-4'}`}
        onClick={handleExpandToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleExpandToggle(e);
          }
        }}
        aria-expanded={isExpanded}
        aria-label={`Feedback #${feedback.id}: ${feedback.title}`}
      >
        {/* Left side: ID, Icon, Title, User Email */}
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          {/* Feedback ID */}
          <span className="text-sm font-mono font-medium text-grey-500 dark:text-grey-400 flex-shrink-0">
            #{feedback.id}
          </span>
          
          {/* Priority Icon */}
          <span 
            className={`text-base ${priorityConfig_item.color} flex-shrink-0`} 
            title={`Priority: ${feedback.priority}`}
          >
            {priorityConfig_item.icon}
          </span>
          
          {/* Title */}
          <h3 className={`font-semibold text-slate-900 dark:text-stone-100 truncate flex-1 ${compact ? 'text-sm' : 'text-base'}`}>
            {feedback.title}
          </h3>
          
          {/* User Email */}
          <span className="text-sm text-grey-600 dark:text-grey-400 flex-shrink-0 hidden sm:block">
            {feedback.user?.email || 'Anonymous'}
          </span>
        </div>

        {/* Right side: Status and Expand Toggle */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <FeedbackStatus status={feedback.status} size="sm" />
          
          {/* Expand/Collapse Toggle */}
          <button
            className={`p-1 rounded-md hover:bg-stone-200 dark:hover:bg-slate-700 transition-all duration-200 ${
              isExpanded ? 'rotate-180' : 'rotate-0'
            }`}
            aria-label={isExpanded ? 'Collapse feedback details' : 'Expand feedback details'}
          >
            <svg className="w-4 h-4 text-grey-500 dark:text-grey-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className={`border-t border-stone-200 dark:border-slate-700 bg-stone-50/50 dark:bg-slate-800/30 ${compact ? 'p-3 space-y-3' : 'p-5 space-y-5'}`}>
          {/* Description */}
          <div>
            <SectionLabel>Description</SectionLabel>
            <div className={PANEL_CLASS}>
              <p className="text-slate-700 dark:text-stone-300 whitespace-pre-wrap leading-relaxed">
                {feedback.description}
              </p>
            </div>
          </div>

          {/* Sentiment Analysis */}
          {!compact && feedback.sentiment_analysis && (
            <SentimentAnalysis sentiment={feedback.sentiment_analysis} />
          )}

          {/* User Information */}
          <div>
            <SectionLabel>User Information</SectionLabel>
            <div className={`flex items-center space-x-3 ${PANEL_CLASS}`}>
              <UserAvatar
                name={feedback.user?.name}
                email={feedback.user?.email}
                picture={feedback.user?.picture}
                size="lg"
              />
              <div className="flex-1">
                <p className="font-medium text-slate-900 dark:text-stone-100">
                  {feedback.user?.name || feedback.user?.email || 'Anonymous User'}
                </p>
                {feedback.user?.email && feedback.user?.name && (
                  <p className="text-sm text-grey-600 dark:text-grey-400">
                    {feedback.user.email}
                  </p>
                )}
                <p className="text-sm text-grey-500 dark:text-grey-400">
                  User ID: {feedback.user_id || 'Not available'}
                </p>
              </div>
            </div>
          </div>

          {/* App Information */}
          {!compact && feedback.app && (
            <div>
              <SectionLabel>Application</SectionLabel>
              <div className={PANEL_CLASS}>
                <div className="flex items-center space-x-3">
                  {feedback.app.logo_uri ? (
                    <img 
                      src={feedback.app.logo_uri} 
                      alt={feedback.app.name}
                      className="w-8 h-8 rounded"
                    />
                  ) : (
                    <span className="text-2xl">📱</span>
                  )}
                  <div>
                    <p className="font-medium text-slate-900 dark:text-stone-100">{feedback.app.name}</p>
                    {feedback.app.description && (
                      <p className="text-sm text-grey-600 dark:text-grey-400">{feedback.app.description}</p>
                    )}
                    <p className="text-xs text-grey-500 dark:text-grey-400">
                      Client ID: {feedback.app.client_id}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Assignment & Resolution */}
          {!compact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Assignment */}
            <div className="flex flex-col">
              <SectionLabel>Assignment</SectionLabel>
              <div className={`${PANEL_CLASS} flex-1`}>
                {feedback.assigned_user ? (
                  <div className="flex items-center space-x-2">
                    <UserAvatar
                      name={feedback.assigned_user.name}
                      email={feedback.assigned_user.email}
                      picture={feedback.assigned_user.picture}
                      size="sm"
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-stone-100">
                        {feedback.assigned_user.name || feedback.assigned_user.email}
                      </p>
                      <p className="text-xs text-grey-500 dark:text-grey-400">
                        Assigned
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-grey-500 dark:text-grey-400 italic">Unassigned</p>
                )}
              </div>
            </div>

            {/* Resolution */}
            <div className="flex flex-col">
              <SectionLabel>Resolution</SectionLabel>
              <div className={`${PANEL_CLASS} flex-1`}>
                {feedback.resolved_at ? (
                  <div>
                    <p className="text-sm font-medium text-green-700 dark:text-green-400">
                      ✅ Resolved
                    </p>
                    <p className="text-xs text-grey-500 dark:text-grey-400">
                      {formatDate(feedback.resolved_at).relative}
                    </p>
                    {feedback.resolver && (
                      <p className="text-xs text-grey-600 dark:text-grey-400">
                        by {feedback.resolver.name || feedback.resolver.email}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-grey-500 dark:text-grey-400 italic">Not resolved</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Management Controls */}
          <div className={`flex flex-wrap items-center gap-x-6 gap-y-3 ${PANEL_CLASS}`}>
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700 dark:text-stone-300">
                Status:
              </label>
              <select
                value={feedback.status}
                onChange={(e) => handleStatusChange(e.target.value as FeedbackStatusType)}
                className="input text-sm w-auto"
              >
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-slate-700 dark:text-stone-300">
                Priority:
              </label>
              <select
                value={feedback.priority}
                onChange={(e) => handlePriorityChange(e.target.value as FeedbackPriorityType)}
                className="input text-sm w-auto"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          {/* Metadata */}
          {!compact && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Timestamps */}
            <div className="flex flex-col">
              <SectionLabel>Timeline</SectionLabel>
              <div className={`${PANEL_CLASS} flex-1 space-y-2`}>
                <div>
                  <p className="text-xs text-grey-500 dark:text-grey-400">Created</p>
                  <p className="text-sm text-slate-700 dark:text-stone-300" title={formatDate(feedback.created_at).absolute}>
                    {formatDate(feedback.created_at).relative}
                  </p>
                </div>
                {feedback.updated_at && (
                  <div>
                    <p className="text-xs text-grey-500 dark:text-grey-400">Last Updated</p>
                    <p className="text-sm text-slate-700 dark:text-stone-300" title={formatDate(feedback.updated_at).absolute}>
                      {formatDate(feedback.updated_at).relative}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* External References */}
            <div className="flex flex-col">
              <SectionLabel>External References</SectionLabel>
              <div className={`${PANEL_CLASS} flex-1 space-y-2`}>
                {feedback.external_id && (
                  <div>
                    <p className="text-xs text-grey-500 dark:text-grey-400">External ID</p>
                    <p className="text-sm text-slate-700 dark:text-stone-300 font-mono">
                      {feedback.external_id}
                    </p>
                  </div>
                )}
                {feedback.source_url && (
                  <div>
                    <p className="text-xs text-grey-500 dark:text-grey-400">Source URL</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 truncate">
                      <a 
                        href={feedback.source_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:underline"
                      >
                        {new URL(feedback.source_url).hostname}
                      </a>
                    </p>
                  </div>
                )}
                {!feedback.external_id && !feedback.source_url && (
                  <p className="text-sm text-grey-500 dark:text-grey-400 italic">No external references</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* Flags */}
          {!compact && (feedback.is_internal !== undefined || feedback.is_public !== undefined) && (
            <div>
              <SectionLabel>Visibility</SectionLabel>
              <div className="flex space-x-2">
                {feedback.is_internal && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-700">
                    🔒 Internal
                  </span>
                )}
                {feedback.is_public && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-700">
                    🌐 Public
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Extra Data */}
          {!compact && feedback.extra_data && Object.keys(feedback.extra_data).length > 0 && (
            <div>
              <SectionLabel>Additional Metadata</SectionLabel>
              <div className={PANEL_CLASS}>
                <pre className="text-xs text-slate-600 dark:text-stone-400 whitespace-pre-wrap overflow-x-auto">
                  {JSON.stringify(feedback.extra_data, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Comments */}
          {!compact && feedback.comments && feedback.comments.length > 0 && (
            <div>
              <SectionLabel>Comments ({feedback.comments.length})</SectionLabel>
              <div className="space-y-3">
                {feedback.comments.map((comment, index) => (
                  <div key={comment.id} className={PANEL_CLASS}>
                    <div className="flex items-center space-x-2 mb-2">
                      <UserAvatar
                        name={getDisplayName(comment.author, comment.user_id)}
                        email={comment.author?.email}
                        picture={comment.author?.picture}
                        size="sm"
                      />
                      <div className="flex-1">
                        <p 
                          className="text-sm font-medium text-slate-900 dark:text-stone-100 cursor-help" 
                          title={getUserTooltip(comment.author, comment.user_id)}
                        >
                          {getDisplayName(comment.author, comment.user_id)}
                        </p>
                        <p className="text-xs text-grey-500 dark:text-grey-400">
                          {formatDate(comment.created_at).relative}
                        </p>
                      </div>
                      {index === 0 && (
                        <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                          Latest
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 dark:text-stone-300 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Button */}
          {onClick && (
            <div className="pt-2 border-t border-stone-200 dark:border-slate-700">
              <button
                onClick={handleCardClick}
                className="btn btn-primary w-full sm:w-auto"
              >
                View Full Details
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackCard;